import { useEffect } from 'react';

import { useEvent, usePrevious } from 'react-use';

import { IS_PRODUCTION, MAX_MESSAGE_LENGTH } from '@/common/constants';
import { stopActiveDictation } from '@/lib/dictation/active-dictation';
import type { ComposerHandle } from '@/lib/dictation/types';

interface UseDictationLifecycleOptions {
  isDictating: boolean;
  composerText: string;
  composerRuntime: ComposerHandle;
  threadId: string;
  truncateToLimit: (text: string) => string;
  triggerCharLimitError: () => void;
  setComposerText: (text: string) => void;
}

/**
 * Hook that manages dictation lifecycle side effects:
 * - Auto-stop when reaching character limit
 * - Stop when switching threads
 * - Stop when tab becomes hidden
 * - Stop on Escape
 * - Sync text to storage during dictation
 */
export function useDictationLifecycle({
  isDictating,
  composerText,
  composerRuntime,
  threadId,
  truncateToLimit,
  triggerCharLimitError,
  setComposerText,
}: UseDictationLifecycleOptions): void {
  const prevThreadId = usePrevious(threadId);
  const prevIsDictating = usePrevious(isDictating);

  // Auto-stop dictation and truncate when reaching MAX_MESSAGE_LENGTH
  useEffect(() => {
    if (isDictating && composerText.length >= MAX_MESSAGE_LENGTH) {
      if (!IS_PRODUCTION) {
        console.info('[Composer] Stopping dictation: reached MAX_MESSAGE_LENGTH');
      }
      composerRuntime.stopDictation();
      const truncated = truncateToLimit(composerText);
      composerRuntime.setText(truncated);
      triggerCharLimitError();
    }
  }, [isDictating, composerText, composerRuntime, truncateToLimit, triggerCharLimitError]);

  // Stop dictation when switching threads
  useEffect(() => {
    if (prevThreadId && prevThreadId !== threadId && prevIsDictating) {
      if (!IS_PRODUCTION) {
        console.info('[Composer] Stopping dictation: thread switched');
      }
      stopActiveDictation();
    }
  }, [threadId, prevThreadId, prevIsDictating]);

  // Stop dictation when tab becomes hidden
  useEffect(() => {
    if (!isDictating) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (!IS_PRODUCTION) {
          console.info('[Composer] Stopping dictation: tab hidden');
        }
        composerRuntime.stopDictation();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isDictating, composerRuntime]);

  // `stopDictation`, not the adapter cancel used on thread switch: the
  // transcript must survive Escape. Undefined handler = no listener bound.
  useEvent(
    'keydown',
    isDictating
      ? (e: KeyboardEvent) => {
          if (e.key !== 'Escape') return;
          if (!IS_PRODUCTION) {
            console.info('[Composer] Stopping dictation: escape pressed');
          }
          composerRuntime.stopDictation();
        }
      : undefined,
  );

  // Sync composer text to persistent storage during dictation
  useEffect(() => {
    if (isDictating && composerText) {
      setComposerText(composerText);
    }
  }, [isDictating, composerText, setComposerText]);
}
